import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Award, Download, Loader2, CheckCircle2, Sparkles, BookOpen, Users, Heart } from "lucide-react";
import { jsPDF } from "jspdf";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import musikonzeptLogo from "@/assets/musikonzept-logo-cropped.png";
import signatureDaniel from "@/assets/unterschrift-daniel-sommer.jpg";
import heroImage from "@/assets/hero-paedagogische-fachkraefte.jpg";
import collageImage from "@/assets/musikonzept-collage.jpg";
import { hatStundenangabe } from "@/lib/zertifikatStunden";
import { Helmet } from "react-helmet-async";
import { istMusikSpracheZertifikat, umfangText } from "@/lib/musikSpracheZertifikat";
import { SUBSCRIPTION_TIERS, TRIAL_PERIOD_DAYS, TRIAL_PRICE_EUR } from "@/config/subscriptionTiers";
import { FACHKRAEFTE_TEXT, LEKTIONEN_TEXT, SEIT_TEXT, gross } from "@/config/webseiteFakten";

const MITGLIED_MONAT = SUBSCRIPTION_TIERS.pro.pricing.monthly.price;
const MITGLIED_JAHR = SUBSCRIPTION_TIERS.pro.pricing.yearly.price;

interface GuestCert {
  id: string;
  certificate_number: string;
  user_name: string;
  course_title: string;
  hours: number | null;
  issued_at: string;
  guest_email: string | null;
  event_date: string | null;
}

const blobToDataUrl = async (blob: Blob): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });

const getImageDimensions = async (src: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = reject;
    image.src = src;
  });

type PublicCertificateRpc = (
  fn: "get_public_certificate_by_token",
  args: { p_guest_token: string }
) => {
  maybeSingle: () => Promise<{ data: GuestCert | null; error: { message: string } | null }>;
};

export default function PublicCertificate() {
  const { token } = useParams<{ token: string }>();
  const [cert, setCert] = useState<GuestCert | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      const getPublicCertificate = supabase.rpc.bind(supabase) as unknown as PublicCertificateRpc;
      const { data, error } = await getPublicCertificate("get_public_certificate_by_token", { p_guest_token: token })
        .maybeSingle();
      if (error || !data) {
        setError("Zertifikat nicht gefunden oder Link ungültig.");
      } else {
        setCert(data);
      }
      setLoading(false);
    })();
  }, [token]);

  const downloadPdf = async () => {
    if (!cert) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    const cx = w / 2;
    doc.setFillColor(255, 253, 248);
    doc.rect(0, 0, w, h, "F");
    doc.setDrawColor(73, 189, 194);
    doc.setLineWidth(2);
    doc.rect(15, 15, w - 30, h - 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(38);
    doc.setTextColor(51, 51, 51);
    doc.text("ZERTIFIKAT", cx, 50, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text("Hiermit wird bestätigt, dass", cx, 75, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(26);
    doc.setTextColor(51);
    doc.text(cert.user_name, cx, 90, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text("an folgender Veranstaltung teilgenommen hat:", cx, 105, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(45, 150, 155);
    const split = doc.splitTextToSize(cert.course_title, w - 80);
    doc.text(split, cx, 118, { align: "center" });
    let y = 118 + split.length * 8;
    if (hatStundenangabe(cert.hours)) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(`Umfang: ${cert.hours} Stunden`, cx, y + 8, { align: "center" });
      y += 8;
    }
    if (cert.event_date) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(
        `Veranstaltungsdatum: ${format(new Date(cert.event_date), "d. MMMM yyyy", { locale: de })}`,
        cx,
        y + 8,
        { align: "center" }
      );
    }
    try {
      const sig = await (await fetch(signatureDaniel)).blob();
      const sigB64 = await blobToDataUrl(sig);
      doc.addImage(sigB64, "JPEG", cx - 25, h - 67, 50, 18);
    } catch { /* ignore */ }
    doc.setDrawColor(150);
    doc.line(cx - 40, h - 47, cx + 40, h - 47);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(51);
    doc.text("Daniel Sommer", cx, h - 41, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text("Leiter von MusiKonzept", cx, h - 36, { align: "center" });
    const issued = format(new Date(cert.issued_at), "d. MMMM yyyy", { locale: de });
    doc.text(`Ausgestellt am ${issued}`, 25, h - 22);
    doc.text(`Zertifikatsnummer: ${cert.certificate_number}`, w - 25, h - 22, { align: "right" });
    try {
      const logo = await (await fetch(musikonzeptLogo)).blob();
      const logoB64 = await blobToDataUrl(logo);
      const { width, height } = await getImageDimensions(logoB64);
      const logoHeight = 18;
      const logoWidth = logoHeight * (width / height);
      doc.addImage(logoB64, "PNG", w - 25 - logoWidth, h - 52, logoWidth, logoHeight);
    } catch { /* ignore */ }
    doc.save(`Zertifikat-${cert.course_title.replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, "-")}.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !cert) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center space-y-4">
            <Award className="h-12 w-12 mx-auto text-muted-foreground/50" />
            <p className="text-muted-foreground">{error || "Unbekannter Fehler."}</p>
            <Link to="/"><Button variant="outline">Zur Startseite</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Gesamtzertifikat Musik und Sprache: Hierher führt der QR-Code „Echtheit prüfen“ – oft von
  // Arbeitgeber:innen geöffnet. Deshalb eine schlichte Prüfansicht: nur Name, Titel, Datum, Nummer,
  // Umfang. Kein Angebot, kein PDF-Download, kein „ZFU“ (die Ausbildung ist nicht ZFU-zugelassen).
  if (istMusikSpracheZertifikat(cert.course_title)) {
    return <PruefAnsicht cert={cert} />;
  }

  const firstName = cert.user_name.split(" ")[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 via-background to-accent/5">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background" aria-hidden />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 pb-10 text-center space-y-4">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-primary to-accent shadow-lg">
            <Award className="h-10 w-10 text-primary-foreground" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            Glückwunsch, {firstName}! 🎉
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Du hast erfolgreich teilgenommen. Dein offizielles MusiKonzept-Teilnahmezertifikat liegt bereit.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pb-16 space-y-8">
        {/* Zertifikats-Karte */}
        <Card className="overflow-hidden border-primary/20 shadow-xl">
          <div className="h-2 bg-gradient-to-r from-primary via-accent to-primary" />
          <CardContent className="py-8 space-y-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Veranstaltung</p>
              <p className="font-bold text-2xl text-foreground">{cert.course_title}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {cert.event_date && (
                <div className="bg-primary/5 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Veranstaltungsdatum</p>
                  <p className="font-semibold">{format(new Date(cert.event_date), "d. MMMM yyyy", { locale: de })}</p>
                </div>
              )}
              {hatStundenangabe(cert.hours) && (
                <div className="bg-primary/5 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Umfang</p>
                  <p className="font-semibold">{cert.hours} Stunden</p>
                </div>
              )}
              <div className="bg-primary/5 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Ausgestellt am</p>
                <p className="font-semibold">{format(new Date(cert.issued_at), "d. MMMM yyyy", { locale: de })}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground font-mono">Nr. {cert.certificate_number}</p>
            <Button
              onClick={downloadPdf}
              size="lg"
              className="w-full gap-2 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground font-semibold shadow-md"
            >
              <Download className="h-5 w-5" />
              Zertifikat als PDF herunterladen
            </Button>
          </CardContent>
        </Card>

        {/* Pro-Angebot CTA */}
        <Card className="overflow-hidden border-accent/30 bg-gradient-to-br from-accent/10 via-background to-primary/10 shadow-lg">
          <CardContent className="py-8 space-y-6">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-accent/20">
                <Sparkles className="h-7 w-7 text-accent" />
              </div>
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
                  Persönliches Angebot für dich
                </div>
                {/* Normale Testphase wie in der Kasse, ohne Rabattcode (Sitzung „Aufräumen“, 14.10.2026; vorher hing
                    MUSIK30 an, also 9 € statt 39 €, während der Text 39 € nannte). */}
                <h2 className="text-2xl font-bold">
                  <span className="text-accent">{TRIAL_PERIOD_DAYS} Tage für {TRIAL_PRICE_EUR} €</span> testen
                </h2>
                <p className="text-muted-foreground">
                  Voller Zugang zu allen Kursen, <strong>{LEKTIONEN_TEXT}</strong> mit Liedern, Spielideen und
                  Konzepten sowie allen Zertifikaten. Nach der Testphase {MITGLIED_MONAT}&nbsp;€ im Monat oder{" "}
                  {MITGLIED_JAHR}&nbsp;€ im Jahr.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { icon: Award, text: "Alle Kurs-Zertifikate inklusive" },
                { icon: BookOpen, text: "Voller Zugang zu allen Kursen" },
                { icon: Heart, text: "Live-Webinare & Community" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-start gap-2 bg-background/60 rounded-lg p-3">
                  <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm font-medium">{text}</p>
                </div>
              ))}
            </div>

            {cert.guest_email && (
              <div className="flex items-center gap-2 text-sm bg-primary/10 rounded-lg p-3">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <p>
                  Nutze beim Checkout <strong>{cert.guest_email}</strong> – dein Zertifikat wird automatisch zugeordnet.
                </p>
              </div>
            )}

            <a
              href="/abo-checkout/pro?mode=trial"
              className="block"
            >
              <Button
                size="lg"
                className="w-full gap-2 bg-gradient-to-r from-accent to-primary hover:opacity-90 text-primary-foreground font-semibold text-base shadow-md"
              >
                <Sparkles className="h-5 w-5" />
                Jetzt für {TRIAL_PRICE_EUR}&nbsp;€ testen
              </Button>
            </a>
            <p className="text-xs text-center text-muted-foreground">
              {TRIAL_PERIOD_DAYS} Tage Testzeit · Danach {MITGLIED_MONAT} € im Monat oder {MITGLIED_JAHR} € im Jahr · Monatsabo monatlich kündbar
            </p>
          </CardContent>
        </Card>

        {/* Über MusiKonzept */}
        <Card className="overflow-hidden border-border/50">
          <div className="grid md:grid-cols-2">
            <div
              className="h-48 md:h-auto bg-cover bg-center"
              style={{ backgroundImage: `url(${collageImage})` }}
              aria-hidden
            />
            <CardContent className="py-8 space-y-3">
              <div className="flex items-center gap-2 text-primary">
                <Users className="h-5 w-5" />
                <p className="text-sm font-semibold uppercase tracking-wider">Über MusiKonzept</p>
              </div>
              <h3 className="text-xl font-bold">Die Online-Akademie für pädagogische Fachkräfte</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {gross(FACHKRAEFTE_TEXT)} wie Erzieher:innen, Lehrkräfte und Tageseltern nutzen MusiKonzept, um
                Kindern Musik mit Freude und Leichtigkeit nahezubringen, {SEIT_TEXT}.
              </p>
              <Link to="/" className="inline-block">
                <Button variant="outline" size="sm">Mehr erfahren</Button>
              </Link>
            </CardContent>
          </div>
        </Card>
      </div>
    </div>
  );
}

function PruefAnsicht({ cert }: { cert: GuestCert }) {
  const zeilen: [string, string][] = [
    ["Name", cert.user_name],
    ["Zertifikat", cert.course_title],
    ["Ausgestellt am", format(new Date(cert.issued_at), "d. MMMM yyyy", { locale: de })],
    ["Zertifikatsnummer", cert.certificate_number],
    ["Umfang", umfangText(cert.hours).replace("Umfang: ", "")],
  ];
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 via-background to-background flex items-start justify-center px-4 py-12 sm:py-20">
      <Helmet>
        <title>Zertifikat geprüft | MusiKonzept</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Card className="w-full max-w-lg overflow-hidden border-primary/20 shadow-lg">
        <div className="h-2 bg-gradient-to-r from-[#BA962E] via-primary to-[#BA962E]" />
        <CardContent className="p-6 sm:p-8 space-y-6">
          <div className="flex items-center justify-between gap-4">
            <img src={musikonzeptLogo} alt="MusiKonzept" className="h-10" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Zertifikat geprüft
            </span>
          </div>
          <p className="text-muted-foreground">Dieses Zertifikat wurde von MusiKonzept ausgestellt und ist gültig.</p>
          <dl className="divide-y divide-border/60 rounded-lg border border-border/60">
            {zeilen.map(([titel, wert]) => (
              <div key={titel} className="grid grid-cols-[9rem_1fr] gap-3 px-4 py-3 text-sm">
                <dt className="text-muted-foreground">{titel}</dt>
                <dd className="font-medium text-foreground">{wert}</dd>
              </div>
            ))}
          </dl>
          <Link to="/ausbildung/fachkraft-musik-und-sprache" className="inline-block text-sm text-primary hover:underline">
            Mehr über die Ausbildung
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
